import { useEffect } from "react";
import { SITE_URL } from "../data/config";
import { SPEC_ROWS } from "../data/products";

const CANONICAL_URL = SITE_URL;
const HERO_IMAGE = new URL("scooters/enhanced/hero-showroom.jpg", CANONICAL_URL).toString();
const LOGO_IMAGE = new URL("evluxe-logo.svg", CANONICAL_URL).toString();
const SEO_TITLE =
  "evLUXE Electric Scooter Manufacturer in India | Dealer, Fleet & Export Enquiries";
const SEO_DESCRIPTION =
  "evLUXE manufactures premium electric scooters in India for dealers, distributors, fleet buyers and export partners. Explore Gitanjali CR2 PRO, CR1 and CR3 electric scooter models, compare specifications and register a B2B enquiry.";

const upsertMeta = (selector, attributes) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
};

const upsertLink = (rel, href, attributes = {}) => {
  let node = document.head.querySelector(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", rel);
    document.head.appendChild(node);
  }
  node.setAttribute("href", href);
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
};

const upsertJsonLd = (id, data) => {
  let node = document.getElementById(id);
  if (!node) {
    node = document.createElement("script");
    node.id = id;
    node.type = "application/ld+json";
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify(data);
};

const absoluteUrl = (url) => {
  if (!url) return undefined;
  return new URL(url, CANONICAL_URL).toString();
};

const productProperties = (product) =>
  SPEC_ROWS.map((row) => ({
    "@type": "PropertyValue",
    name: row.label,
    value: product.specs[row.key],
  })).filter((item) => item.value);

const buildStructuredData = (products, content) => {
  const contact = content.contact;
  const faqs = content.faqSection?.items || [];

  const organization = {
    "@type": "Organization",
    "@id": `${CANONICAL_URL}#organization`,
    name: "evLUXE",
    legalName: "evLUXE",
    url: CANONICAL_URL,
    logo: LOGO_IMAGE,
    image: HERO_IMAGE,
    description:
      "evLUXE is an Indian electric scooter manufacturer serving consumer, dealer, distributor, fleet and export enquiries.",
    foundingDate: "1960",
    slogan: "Electric scooters built for India",
    brand: { "@id": `${CANONICAL_URL}#brand` },
    areaServed: ["IN", "International"],
    knowsAbout: [
      "electric scooters",
      "EV scooter dealership",
      "electric scooter distributor network",
      "fleet electric scooters",
      "BLDC hub motor scooters",
      "lithium-ion electric scooters",
      "lead-acid electric scooters",
      "Indian electric mobility",
    ],
    address: [
      {
        "@type": "PostalAddress",
        name: contact.corporateOffice.label,
        streetAddress: "No.392 Ghitorni, Pillar No:114 M.G. Road",
        addressLocality: "New Delhi",
        postalCode: "110030",
        addressCountry: "IN",
      },
      {
        "@type": "PostalAddress",
        name: contact.manufacturingUnit.label,
        streetAddress: "Plot No. 5, Sector 7B, North Avenue, Reliance Model Economic Township",
        addressLocality: "Jhajjar",
        addressRegion: "Haryana",
        addressCountry: "IN",
      },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: contact.phones?.[0]?.tel,
        email: contact.email,
        contactType: "sales",
        areaServed: "IN",
        availableLanguage: ["en", "hi"],
      },
      {
        "@type": "ContactPoint",
        telephone: contact.phones?.[0]?.tel,
        email: contact.email,
        contactType: "dealer and distributor enquiries",
        areaServed: ["IN", "International"],
        availableLanguage: ["en", "hi"],
      },
      {
        "@type": "ContactPoint",
        telephone: contact.phones?.[1]?.tel,
        email: contact.email,
        contactType: "fleet and export enquiries",
        areaServed: ["IN", "International"],
        availableLanguage: ["en", "hi"],
      },
    ],
  };

  const productSchemas = products.map((product, index) => ({
    "@type": "Product",
    "@id": `${CANONICAL_URL}#product-${product.id}`,
    name: `evLUXE ${product.name}`,
    model: product.name,
    sku: `evLUXE-${product.id.toUpperCase()}`,
    position: index + 1,
    brand: { "@id": `${CANONICAL_URL}#brand` },
    manufacturer: { "@id": `${CANONICAL_URL}#organization` },
    category: "Electric scooter",
    vehicleConfiguration: "Electric scooter",
    fuelType: "Electric",
    image: product.images.map(absoluteUrl),
    color: product.colorNames,
    description: `${product.description} Suitable for evLUXE dealer, distributor, fleet and bulk purchase enquiries.`,
    additionalProperty: productProperties(product),
    audience: [
      { "@type": "BusinessAudience", audienceType: "Dealers and distributors" },
      { "@type": "BusinessAudience", audienceType: "Fleet and business buyers" },
      { "@type": "BusinessAudience", audienceType: "Export partners" },
    ],
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      organization,
      {
        "@type": "Brand",
        "@id": `${CANONICAL_URL}#brand`,
        name: "evLUXE",
        url: CANONICAL_URL,
        logo: LOGO_IMAGE,
      },
      {
        "@type": "WebSite",
        "@id": `${CANONICAL_URL}#website`,
        url: CANONICAL_URL,
        name: "evLUXE",
        alternateName: "evLUXE Electric Scooters",
        publisher: { "@id": `${CANONICAL_URL}#organization` },
        inLanguage: "en-IN",
      },
      {
        "@type": "WebPage",
        "@id": `${CANONICAL_URL}#webpage`,
        url: CANONICAL_URL,
        name: SEO_TITLE,
        description: SEO_DESCRIPTION,
        isPartOf: { "@id": `${CANONICAL_URL}#website` },
        about: { "@id": `${CANONICAL_URL}#organization` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: HERO_IMAGE,
        },
        inLanguage: "en-IN",
      },
      {
        "@type": "ItemList",
        "@id": `${CANONICAL_URL}#electric-scooter-catalogue`,
        name: "evLUXE Gitanjali electric scooter catalogue",
        description:
          "evLUXE electric scooter models available for dealership, distributor, fleet, business buyer and export enquiries.",
        numberOfItems: products.length,
        itemListElement: products.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${CANONICAL_URL}#models`,
          item: { "@id": `${CANONICAL_URL}#product-${product.id}` },
        })),
      },
      {
        "@type": "Service",
        "@id": `${CANONICAL_URL}#b2b-sales-service`,
        name: "evLUXE B2B electric scooter sales",
        serviceType: "Electric scooter dealership, distributor, fleet, bulk and export enquiry support",
        provider: { "@id": `${CANONICAL_URL}#organization` },
        areaServed: ["IN", "International"],
        audience: [
          { "@type": "BusinessAudience", audienceType: "Dealer / Distributor" },
          { "@type": "BusinessAudience", audienceType: "Fleet / Business Buyer" },
          { "@type": "BusinessAudience", audienceType: "Export Enquiry" },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${CANONICAL_URL}#faq`,
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${CANONICAL_URL}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: CANONICAL_URL,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Electric Scooter Models",
            item: `${CANONICAL_URL}#models`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "B2B Enquiry",
            item: `${CANONICAL_URL}#register`,
          },
        ],
      },
      ...productSchemas,
    ],
  };
};

export const Seo = ({ products, content }) => {
  useEffect(() => {
    document.documentElement.lang = "en-IN";
    document.title = SEO_TITLE;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: SEO_DESCRIPTION,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
    });
    upsertMeta('meta[name="keywords"]', {
      name: "keywords",
      content:
        "evLUXE electric scooter manufacturer India, electric scooter dealership India, EV scooter distributor, electric scooter fleet sales, bulk electric scooters, evLUXE CR2 PRO, Gitanjali electric scooter, lithium ion scooter India, BLDC hub motor scooter",
    });
    upsertMeta('meta[name="author"]', { name: "author", content: "evLUXE" });
    upsertMeta('meta[name="geo.region"]', { name: "geo.region", content: "IN-DL" });
    upsertMeta('meta[name="geo.placename"]', { name: "geo.placename", content: "New Delhi, India" });
    upsertMeta('meta[name="application-name"]', { name: "application-name", content: "evLUXE" });

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "evLUXE" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: SEO_TITLE });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: SEO_DESCRIPTION });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: CANONICAL_URL });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: HERO_IMAGE });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: "evLUXE premium electric scooter for Indian roads",
    });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_IN" });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: SEO_TITLE });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: SEO_DESCRIPTION });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: HERO_IMAGE });

    upsertLink("canonical", CANONICAL_URL);
    upsertLink("sitemap", `${CANONICAL_URL}sitemap.xml`, { type: "application/xml" });
    upsertLink("alternate", CANONICAL_URL, { hreflang: "en-IN" });

    upsertJsonLd("evluxe-structured-data", buildStructuredData(products, content));
  }, [products, content]);

  return null;
};

export default Seo;
